import type { ImageContent } from "@homeinstead/schemas";

import { cn } from "@homeinstead/ui";
import Image from "next/image";
import { urlFor } from "~www/lib/sanity/sanity-images";
import { ApplyToBeACareGiverSection } from "../CareGiverApplyButton";
import {
  DEFAULT_HERO_IMAGES,
  getMobileWrapperMinHeight,
  resolveAssetDimensions,
} from "./hero-utils";

type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

interface HeroImageProps {
  breakpoint?: Breakpoint;
  showLargeViewportImage: boolean;
  showSmallViewportImage?: boolean;
  showCareGiverCTA?: boolean;
  heroImageLarge?: ImageContent;
  heroImageSmall?: ImageContent;
  /** Merged onto the desktop image wrapper. */
  largeViewportClassName?: string;
}

type HeroImageSizes = "large" | "small";

const DESKTOP_WRAPPER_CLASS: Record<Breakpoint, string> = {
  "2xl": "hidden 2xl:flex 2xl:flex-col 2xl:items-start",
  lg: "hidden lg:flex lg:flex-col lg:items-start",
  md: "hidden md:flex md:flex-col md:items-start",
  sm: "hidden sm:flex sm:flex-col sm:items-start",
  xl: "hidden xl:flex xl:flex-col xl:items-start",
};

const MOBILE_WRAPPER_CLASS: Record<Breakpoint, string> = {
  "2xl": "block 2xl:hidden",
  lg: "block lg:hidden",
  md: "block md:hidden",
  sm: "block sm:hidden",
  xl: "block xl:hidden",
};

const HERO_IMAGES_SIZES: {
  [key in HeroImageSizes]: { width: number; height: number };
} = {
  large: {
    height: 720,
    width: 936,
  },
  small: {
    height: 1066,
    width: 1600,
  },
} as const;

const DEFAULT_ALT_TEXT = "A Home Instead Care Pro with an adult.";

export const HeroImage = ({
  breakpoint = "xl",
  showLargeViewportImage,
  showSmallViewportImage = true,
  showCareGiverCTA = true,
  heroImageLarge,
  heroImageSmall,
  largeViewportClassName,
}: HeroImageProps) => {
  // Use Sanity images if available, otherwise fall back to defaults
  const largeImage = heroImageLarge?.image;
  const smallImage = heroImageSmall?.image;

  const largeImageUrl = urlFor(
    largeImage?.asset ?? DEFAULT_HERO_IMAGES.large,
  ).url();
  const smallImageUrl = urlFor(
    smallImage?.asset ?? DEFAULT_HERO_IMAGES.small,
  ).url();

  const largeImageAlt = largeImage?.alt || DEFAULT_ALT_TEXT;
  const smallImageAlt = smallImage?.alt || DEFAULT_ALT_TEXT;
  const largeImageDimensions = resolveAssetDimensions(
    largeImage?.asset,
    HERO_IMAGES_SIZES.large,
  );
  const smallImageDimensions = resolveAssetDimensions(
    smallImage?.asset,
    HERO_IMAGES_SIZES.small,
  );

  return (
    <>
      {/* Desktop image container with overflow hidden for cropping */}
      {showLargeViewportImage && (
        <div
          className={cn(
            DESKTOP_WRAPPER_CLASS[breakpoint],
            largeViewportClassName,
            // When the caregiver CTA row is hidden, restore similar vertical rhythm to my-4 on that row.
            !showCareGiverCTA && "pb-4",
          )}
        >
          <div className="w-full overflow-hidden">
            <Image
              alt={largeImageAlt}
              className="block w-full object-cover object-center"
              height={largeImageDimensions.height}
              loading="eager"
              priority
              src={largeImageUrl}
              style={{
                minHeight: largeImageDimensions.height,
                minWidth: largeImageDimensions.width,
              }}
              width={largeImageDimensions.width}
            />
          </div>
          {showCareGiverCTA && (
            <div className="my-4 flex w-full justify-center gap-4">
              <ApplyToBeACareGiverSection
                buttonClassName="font-bold"
                className="pr-16"
              />
            </div>
          )}
        </div>
      )}
      {showSmallViewportImage && (
        <div
          className={cn(
            MOBILE_WRAPPER_CLASS[breakpoint],
            !showCareGiverCTA && "pb-4",
          )}
          style={{
            minHeight: getMobileWrapperMinHeight(
              smallImageDimensions,
              showCareGiverCTA,
            ),
          }}
        >
          <Image
            alt={smallImageAlt}
            className="w-full"
            height={smallImageDimensions.height}
            loading="eager"
            src={smallImageUrl}
            width={smallImageDimensions.width}
          />
          {showCareGiverCTA && (
            <div className="my-4 flex justify-center gap-4">
              <ApplyToBeACareGiverSection />
            </div>
          )}
        </div>
      )}
    </>
  );
};
